import React, { useState } from 'react';
import { X, Clock, Link2, ExternalLink, Edit2, Save } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const NoteDetailModal = ({ note, isOpen, onClose, onUpdate }) => {
  const navigate = useNavigate();
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState(note?.summary || '');
  const [saving, setSaving] = useState(false);

  // Update edited summary when note changes
  React.useEffect(() => {
    if (note) {
      setEditedSummary(note.summary || '');
      setIsEditingSummary(false);
    }
  }, [note]);

  if (!isOpen || !note) return null;

  const formatDate = (dateStr) => {
    // Parse date string - handle ISO format and extract date-only value
    let date;
    if (typeof dateStr === 'string') {
      // For created_at and similar timestamps, convert UTC to local first, then extract date
      if (dateStr.includes('T')) {
        // Parse as UTC timestamp and convert to local date
        const utcDate = new Date(dateStr);
        // Extract local date components (not UTC date)
        const year = utcDate.getFullYear();
        const month = utcDate.getMonth();
        const day = utcDate.getDate();
        // Create date in local timezone using the local date components
        date = new Date(year, month, day);
      } else {
        // Already a date-only string (YYYY-MM-DD)
        const [year, month, day] = dateStr.split('-').map(Number);
        date = new Date(year, month - 1, day);
      }
    } else {
      date = new Date(dateStr);
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleSaveSummary = async () => {
    if (!note || !note.id) return;
    
    setSaving(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const API = `${API_URL}/api`;
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${API}/notes/${note.id}`,
        { summary: editedSummary },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Update local note state with updated note from response
      if (response.data && response.data.note) {
        // Update the note prop by calling onUpdate which will refetch
        if (onUpdate) {
          await onUpdate();
        }
      }
      
      toast.success('Summary updated successfully');
      setIsEditingSummary(false);
    } catch (error) {
      console.error('Error updating summary:', error);
      const errorMessage = error.response?.data?.detail || error.response?.data?.message || 'Failed to update summary';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleViewEntry = () => {
    if (note.time_entry_id) {
      // Navigate to calendar with the entry selected
      navigate(`/calendar?entry=${note.time_entry_id}`);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Container - Centered */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        {/* Modal - Centered */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col border border-gray-200 dark:border-gray-700 transition-colors">
          {/* Header - Fixed */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 flex-shrink-0 border-b border-gray-200 dark:border-gray-700 transition-colors">
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 mb-1 transition-colors">
                  {note.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1 transition-colors">
                  <Clock className="w-4 h-4" />
                  {formatDate(note.meeting_date)}
                  {note.meeting_time && ` at ${formatTime(note.meeting_time)}`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-300 min-w-[44px] min-h-[44px] flex items-center justify-center ml-4 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Project */}
            {note.project && (
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200 dark:border-gray-700 transition-colors">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: note.project.color }}
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                  {note.project.name}
                </span>
              </div>
            )}
          </div>

          {/* Content - Scrollable */}
          <div className="px-4 pt-4 pb-4 sm:px-6 sm:pt-6 sm:pb-6 overflow-y-auto flex-1 min-h-0 bg-white dark:bg-gray-800 transition-colors">
            {/* Full Transcript */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors">
                📝 Full Transcript
              </h4>
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-md max-h-80 overflow-y-auto border border-gray-200 dark:border-gray-600 transition-colors">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed transition-colors">
                  {note.transcript}
                </p>
              </div>
            </div>

            {/* AI Summary - Editable */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors">
                  ✨ AI Summary
                </h4>
                {!isEditingSummary && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingSummary(true)}
                    className="h-7 px-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              {isEditingSummary ? (
                <div className="space-y-2">
                  <Textarea
                    value={editedSummary}
                    onChange={(e) => setEditedSummary(e.target.value)}
                    className="min-h-[120px] resize-y bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-400 transition-colors"
                    rows={5}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveSummary}
                      disabled={saving}
                      size="sm"
                      className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                      <Save className="w-3 h-3 mr-1" />
                      {saving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      onClick={() => {
                        setEditedSummary(note.summary || '');
                        setIsEditingSummary(false);
                      }}
                      variant="outline"
                      size="sm"
                      disabled={saving}
                      className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-800 transition-colors">
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap transition-colors">
                    {note.summary}
                  </p>
                </div>
              )}
            </div>

            {/* Time Entry Link */}
            {note.time_entry_id || note.time_entry ? (
              <div className="p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md mb-4 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-800 dark:text-green-300 transition-colors">
                      Linked to time entry: {note.time_entry?.duration_minutes 
                        ? `${(note.time_entry.duration_minutes / 60).toFixed(1)}h`
                        : 'time entry'}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-green-700 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                    onClick={handleViewEntry}
                  >
                    View Entry →
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Metadata */}
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 transition-colors">
              <div>
                Recorded: {formatDate(note.created_at)}
              </div>
              {note.recording_duration_seconds && (
                <div>
                  Recording Duration: {formatDuration(note.recording_duration_seconds)}
                </div>
              )}
            </div>
          </div>

          {/* Footer - Fixed */}
          <div className="bg-gray-50 dark:bg-gray-700/50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-2 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 transition-colors">
            <Button
              onClick={onClose}
              className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                // Export as text file
                const content = `Title: ${note.title}\n\nDate: ${formatDate(note.meeting_date)}\n${note.meeting_time ? `Time: ${formatTime(note.meeting_time)}\n` : ''}${note.project ? `Project: ${note.project.name}\n` : ''}\n\nSummary:\n${note.summary}\n\nFull Transcript:\n${note.transcript}`;
                
                const blob = new Blob([content], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${note.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date(note.meeting_date).toISOString().split('T')[0]}.txt`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
                toast.success('Note exported as text file!');
              }}
              variant="outline"
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              📄 Export as Text
            </Button>
            <Button
              onClick={() => {
                // Copy to clipboard
                const content = `Title: ${note.title}\n\nDate: ${formatDate(note.meeting_date)}\n${note.meeting_time ? `Time: ${formatTime(note.meeting_time)}\n` : ''}${note.project ? `Project: ${note.project.name}\n` : ''}\n\nSummary:\n${note.summary}\n\nFull Transcript:\n${note.transcript}`;
                navigator.clipboard.writeText(content).then(() => {
                  toast.success('Note copied to clipboard!');
                }).catch(() => {
                  toast.error('Failed to copy to clipboard');
                });
              }}
              variant="outline"
              className="w-full sm:w-auto border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              📋 Copy to Clipboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoteDetailModal;
